import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Wrench } from "lucide-react";

export function FunnelDiagnosisCard() {
  const [rows, setRows] = useState(null);

  const load = () => api.get("/campaigns/funnel").then(({ data }) => setRows(data.rows)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const retarget = async (code) => {
    try {
      await api.patch(`/campaigns/${code}`, { destination: "/" });
      toast.success("Link now lands on the campaign-aware homepage");
      load();
    } catch { toast.error("Could not update link"); }
  };

  if (!rows) return null;

  return (
    <div className="mt-6 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="funnel-diagnosis">
      <div className="text-xs uppercase tracking-widest text-neutral-500">Clicks &rarr; signups</div>
      <h3 className="font-black text-xl tracking-tight mt-1">Why clicks aren't converting</h3>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-neutral-500" data-testid="funnel-empty">No tracked-link clicks yet.</p>
      ) : (
        <ul className="mt-4 space-y-4">
          {rows.map((r) => (
            <li key={r.code} className="rounded-lg border border-[#27272A] p-4" data-testid={`funnel-row-${r.code}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-white">{r.name}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {r.channel} &middot; lands on <span className="font-mono text-neutral-400">{r.destination}</span>
                    {r.category_label && <> &middot; audience: {r.category_label} ({r.live_in_category} live)</>}
                  </div>
                </div>
                <div className="flex gap-5 text-sm font-mono tabular-nums">
                  <Num label="clicks" v={r.clicks} c="text-[#34C759]" />
                  <Num label="bots" v={r.bot_clicks} c="text-neutral-500" />
                  <Num label="visits" v={r.visits} c="text-neutral-400" />
                  <Num label="signups" v={r.signups} c={r.signups ? "text-[#007AFF]" : "text-[#FF3B30]"} />
                </div>
              </div>
              {r.diagnosis.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {r.diagnosis.map((d, i) => (
                    <li key={i} className="flex gap-2 text-xs text-neutral-300">
                      {r.signups > 0 ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-[#34C759] shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-[#FF9F0A] shrink-0" />}
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              )}
              {r.destination !== "/" && r.signups === 0 && (
                <button onClick={() => retarget(r.code)} data-testid={`funnel-retarget-${r.code}`} className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 hover:bg-white/5">
                  <Wrench className="h-3 w-3" /> Point link at campaign-aware homepage
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Num({ label, v, c }) {
  return (
    <div className="text-right">
      <div className={`font-bold ${c}`}>{v}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-600">{label}</div>
    </div>
  );
}
